const STORAGE_KEY = 'offworld.window-layout.v1';
const GAP = 8;
const CHAT_MIN_WIDTH = 280;
const CHAT_MIN_HEIGHT = 240;
type Corner = 'nw' | 'ne' | 'sw' | 'se';
interface Position { x: number; y: number; width?: number; height?: number }
interface Box { x: number; y: number; width: number; height: number }
interface ManagedWindow { element: HTMLElement; floating: boolean; handle: HTMLElement; preferred?: Position }
interface Gesture { window: ManagedWindow; handle: HTMLElement; pointer: number; startX: number; startY: number; box: Box; corner?: Corner; changed: boolean }
const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(min, max), Math.max(min, value));

/** Presentation-only window geometry; defaults remain in CSS and reset never touches form state. */
export class WindowLayout {
  private windows: ManagedWindow[] = [];
  private events = new AbortController();
  private observer: ResizeObserver;
  private gesture: Gesture | null = null;
  private frame = 0;

  constructor(private stage: HTMLElement, private stopInput: () => void) {
    const saved = this.load(), options = { signal: this.events.signal };
    for (const element of document.querySelectorAll<HTMLElement>('.floating-panel, dialog')) {
      const handle = element.querySelector<HTMLElement>('.panel-heading') ?? element.querySelector<HTMLElement>('h2');
      if (!handle || !element.id) continue;
      const item: ManagedWindow = { element, handle, floating: element.classList.contains('floating-panel'), preferred: saved[element.id] };
      this.windows.push(item); handle.classList.add('window-drag-handle'); handle.tabIndex = 0;
      handle.title = 'Drag to move · arrow keys move when the title is focused';
      handle.addEventListener('pointerdown', event => this.start(item, handle, event), options);
      handle.addEventListener('keydown', event => this.keyboard(item, event), options);
      handle.addEventListener('focus', () => this.stopInput(), options);
      if (element.id === 'chat-panel') for (const corner of ['nw', 'ne', 'sw', 'se'] as const) {
        const resize = document.createElement('button'); resize.type = 'button'; resize.className = 'window-resize-handle'; resize.dataset.corner = corner;
        const label = `Resize chat from ${corner[0] === 'n' ? 'top' : 'bottom'} ${corner[1] === 'w' ? 'left' : 'right'}`;
        resize.setAttribute('aria-label', label); resize.title = `${label} · minimum width ${CHAT_MIN_WIDTH}px · arrow keys resize`;
        resize.addEventListener('pointerdown', event => this.start(item, resize, event, corner), options);
        resize.addEventListener('keydown', event => this.keyboard(item, event, corner), options); element.append(resize);
      }
    }
    document.addEventListener('pointermove', event => this.move(event), options);
    document.addEventListener('pointerup', event => { if (event.pointerId === this.gesture?.pointer) this.finish(); }, options);
    document.addEventListener('pointercancel', () => this.finish(), options);
    window.addEventListener('blur', () => this.finish(), options);
    document.addEventListener('visibilitychange', () => this.finish(), options);
    window.addEventListener('resize', () => { this.finish(); this.scheduleFit(); }, options);
    document.getElementById('reset-window-layout')!.addEventListener('click', () => this.reset(), options);
    this.observer = new ResizeObserver(() => this.scheduleFit()); this.observer.observe(stage);
    for (const item of this.windows) this.observer.observe(item.element);
    this.scheduleFit();
  }

  private visible(item: ManagedWindow): boolean { return !item.element.hidden && (!(item.element instanceof HTMLDialogElement) || item.element.open) && item.element.getClientRects().length > 0; }
  private bounds(item: ManagedWindow): { left: number; top: number; width: number; height: number } {
    return item.floating ? this.stage.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  }
  private box(item: ManagedWindow): Box {
    const rect = item.element.getBoundingClientRect(), bounds = this.bounds(item);
    return { x: rect.left - bounds.left, y: rect.top - bounds.top, width: rect.width, height: rect.height };
  }
  private start(item: ManagedWindow, handle: HTMLElement, event: PointerEvent, corner?: Corner): void {
    if (event.button !== 0 || (!corner && (event.target as HTMLElement).closest('button, input, select, a'))) return;
    this.finish(); this.stopInput(); event.preventDefault(); handle.focus({ preventScroll: true });
    this.gesture = { window: item, handle, pointer: event.pointerId, startX: event.clientX, startY: event.clientY, box: this.box(item), corner, changed: false };
    handle.setPointerCapture(event.pointerId); item.element.classList.add('window-manipulating');
  }
  private move(event: PointerEvent): void {
    const gesture = this.gesture; if (!gesture || event.pointerId !== gesture.pointer) return;
    if (!this.visible(gesture.window)) { this.finish(); return; }
    const dx = event.clientX - gesture.startX, dy = event.clientY - gesture.startY;
    if (!gesture.changed && Math.hypot(dx, dy) < 2) return;
    event.preventDefault(); gesture.changed = true;
    this.adjust(gesture.window, gesture.box, dx, dy, gesture.corner);
  }
  private keyboard(item: ManagedWindow, event: KeyboardEvent, corner?: Corner): void {
    if (event.target !== event.currentTarget) return;
    const step = event.shiftKey ? 40 : 10;
    const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
    const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
    if (!dx && !dy) return;
    event.preventDefault(); event.stopPropagation(); this.stopInput(); this.adjust(item, this.box(item), dx, dy, corner); this.save();
  }
  private adjust(item: ManagedWindow, box: Box, dx: number, dy: number, corner?: Corner): void {
    const bounds = this.bounds(item);
    if (!corner) {
      item.preferred = { ...item.preferred, x: clamp(box.x + dx, GAP, bounds.width - box.width - GAP), y: clamp(box.y + dy, GAP, bounds.height - box.height - GAP) };
    } else {
      const west = corner.endsWith('w'), north = corner.startsWith('n');
      const minWidth = Math.min(CHAT_MIN_WIDTH, bounds.width - GAP * 2), minHeight = Math.min(CHAT_MIN_HEIGHT, bounds.height - GAP * 2);
      const width = clamp(box.width + (west ? -dx : dx), minWidth, west ? box.x + box.width - GAP : bounds.width - box.x - GAP);
      const height = clamp(box.height + (north ? -dy : dy), minHeight, north ? box.y + box.height - GAP : bounds.height - box.y - GAP);
      item.preferred = { x: west ? box.x + box.width - width : box.x, y: north ? box.y + box.height - height : box.y, width, height };
    }
    this.fit(item);
  }
  private finish(): void {
    const gesture = this.gesture; if (!gesture) return; this.gesture = null;
    if (gesture.handle.hasPointerCapture(gesture.pointer)) gesture.handle.releasePointerCapture(gesture.pointer);
    gesture.window.element.classList.remove('window-manipulating'); if (gesture.changed) this.save();
  }
  private scheduleFit(): void {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; for (const item of this.windows) if (this.visible(item)) this.fit(item); });
  }
  private fit(item: ManagedWindow): void {
    const preferred = item.preferred; if (!preferred) return;
    const bounds = this.bounds(item), style = item.element.style;
    if (preferred.width !== undefined) style.width = `${Math.min(preferred.width, bounds.width - GAP * 2)}px`;
    if (preferred.height !== undefined) style.height = `${Math.min(preferred.height, bounds.height - GAP * 2)}px`;
    const rect = item.element.getBoundingClientRect();
    style.left = `${clamp(preferred.x, GAP, bounds.width - rect.width - GAP)}px`;
    style.top = `${clamp(preferred.y, GAP, bounds.height - rect.height - GAP)}px`;
    style.right = 'auto'; style.bottom = 'auto';
    if (!item.floating) { style.position = 'fixed'; style.margin = '0'; }
  }

  reset(): void {
    this.finish();
    for (const item of this.windows) {
      item.preferred = undefined;
      for (const property of ['left', 'top', 'right', 'bottom', 'width', 'height', 'margin', 'position']) item.element.style.removeProperty(property);
    }
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* Layout storage is optional. */ }
    document.getElementById('window-layout-status')!.textContent = 'Window positions and sizes reset.';
  }
  private load(): Record<string, Position> {
    const result: Record<string, Position> = {};
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
      if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return result;
      for (const [id, value] of Object.entries(saved)) {
        if (!value || typeof value !== 'object') continue;
        const v = value as Record<string, unknown>;
        const coordinate = (key: string): boolean => typeof v[key] === 'number' && Number.isFinite(v[key]) && Math.abs(v[key] as number) <= 10000;
        if (!coordinate('x') || !coordinate('y')) continue;
        const position: Position = { x: v.x as number, y: v.y as number };
        if (id === 'chat-panel' && coordinate('width') && coordinate('height')) {
          position.width = Math.max(CHAT_MIN_WIDTH, v.width as number); position.height = Math.max(CHAT_MIN_HEIGHT, v.height as number);
        }
        result[id] = position;
      }
    } catch { /* Corrupt or unavailable storage falls back to CSS defaults. */ }
    return result;
  }
  private save(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(this.windows.filter(item => item.preferred).map(item => [item.element.id, item.preferred])))); } catch { /* Layout storage is optional. */ }
  }
  dispose(): void { this.finish(); this.events.abort(); this.observer.disconnect(); cancelAnimationFrame(this.frame); }
}
